// EintragDialog.tsx — Popup zum Anlegen/Bearbeiten EINES Listeneintrags in der
// Kontaktanlage (Beziehungen, Verbindungen, Verantwortung; User 26.09.2026).
//
// Die Felder kommen unverändert aus der jeweiligen Liste (`renderFelder`) — der
// Dialog hält nur einen lokalen Entwurf und gibt ihn mit „Übernehmen" zurück.
// Optik wie ConfirmDialog: Portal an document.body, REFINED-Tokens inline.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

export function EintragDialog({
  titel,
  kicker = "Eintrag",
  children,
  fehler,
  uebernehmenGesperrt,
  onUebernehmen,
  onAbbrechen,
  nurAnsehen = false,
  ansehenHinweis = "Gesperrt — zum Ändern das Schloss oben rechts öffnen.",
  uebernehmenText = "Übernehmen",
  fussExtra,
  beschaeftigt = false,
}: {
  titel: string;
  kicker?: string;
  children: React.ReactNode;
  /** Meldung unter den Feldern (z. B. ungültige IBAN). */
  fehler?: string | null;
  uebernehmenGesperrt?: boolean;
  onUebernehmen: () => void;
  onAbbrechen: () => void;
  /**
   * Gesperrte Akte: Felder nur zeigen (eigenes `fieldset disabled` — das Popup
   * hängt per Portal an document.body und läge sonst außerhalb der Sperre),
   * kein „Übernehmen", nur „Schließen".
   */
  nurAnsehen?: boolean;
  /** Hinweis über den Feldern im Nur-Ansehen-Modus (`null` = keiner). */
  ansehenHinweis?: string | null;
  /** Beschriftung des Bestätigen-Knopfs (z. B. „Speichern" beim Sofort-Speichern). */
  uebernehmenText?: string;
  /** Weiterer Knopf im Fuß, links neben „Schließen" (z. B. „Daten ändern"). */
  fussExtra?: React.ReactNode;
  /** Speichert gerade — Bestätigen gesperrt. */
  beschaeftigt?: boolean;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onAbbrechen();
    };
    alleDokumente.addEventListener("keydown", onKey);
    return () => alleDokumente.removeEventListener("keydown", onKey);
  }, [onAbbrechen]);

  const knopf: React.CSSProperties = {
    appearance: "none",
    borderRadius: 10,
    padding: "9px 16px",
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    cursor: "pointer",
  };

  return createPortal(
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        // Unter den Portal-Listen der Felder (Verantwortlichkeit, Branche:
        // 100000) und den Dialogen darüber (Daten befüllen, Bestätigen: 100002),
        // über der App-Shell.
        zIndex: 99990,
        background: "rgba(20,8,20,0.34)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        fontFamily: REFINED.fontUI,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        style={{
          width: "min(720px, 100%)",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          background: REFINED.white,
          border: `1px solid ${REFINED.line}`,
          borderRadius: 14,
          boxShadow: REFINED.cardShadow,
          color: REFINED.ink,
        }}
      >
        <div style={{ padding: "15px 18px", borderBottom: `2px solid ${REFINED.goldLine}` }}>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: REFINED.ink3,
            }}
          >
            {nurAnsehen ? "Ansehen" : kicker}
          </div>
          <h3 style={{ margin: "2px 0 0", fontSize: REFINED.fs.lg, fontWeight: 800 }}>{titel}</h3>
        </div>
        {/* Scrollt selbst — die Such-Popover der Felder sollen darin bleiben. */}
        <div
          style={{
            padding: "16px 18px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            rowGap: 16,
          }}
        >
          {nurAnsehen ? (
            <>
              {ansehenHinweis && (
                <div role="note" style={{ fontSize: "0.82rem", color: REFINED.ink2, fontStyle: "italic" }}>
                  {ansehenHinweis}
                </div>
              )}
              <fieldset disabled style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: "flex", flexDirection: "column", rowGap: 16 }}>
                {children}
              </fieldset>
            </>
          ) : (
            children
          )}
          {!nurAnsehen && fehler && (
            <div role="alert" style={{ color: REFINED.err, fontSize: "0.82rem" }}>
              {fehler}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "12px 18px",
            borderTop: `1px solid ${REFINED.line}`,
            background: REFINED.fieldBg,
            borderRadius: "0 0 14px 14px",
          }}
        >
          {fussExtra}
          <button
            type="button"
            onClick={onAbbrechen}
            style={{ ...knopf, border: `1.5px solid ${REFINED.line}`, background: REFINED.white, color: REFINED.ink }}
          >
            {nurAnsehen ? "Schließen" : "Abbrechen"}
          </button>
          {!nurAnsehen && (
          <button
            type="button"
            onClick={onUebernehmen}
            disabled={uebernehmenGesperrt || beschaeftigt}
            style={{
              ...knopf,
              border: `1.5px solid ${REFINED.aub}`,
              background: REFINED.aub,
              color: REFINED.white,
              opacity: uebernehmenGesperrt || beschaeftigt ? 0.45 : 1,
              cursor: uebernehmenGesperrt || beschaeftigt ? "not-allowed" : "pointer",
            }}
          >
            {uebernehmenText}
          </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
